import { Injectable } from '@angular/core';
import { CommonApiErrorHandlerService } from './common-api-error-handler.service';
import { runtimeConfig } from '../utils/config-utils';
import * as _ from 'lodash';
import 'rxjs/add/operator/toPromise';
import {
  HttpClient, HttpHeaders, HttpParams
} from '@angular/common/http';
import { RequestConfiguration } from '../models/request-configuration.model';
import { ClientRequestOptions } from '../models/client-request-options.model';
import { GetParamsExpandableArrayWrapper } from '../utils/get-params-expandable-array-wrapper';
import {ClientRequestMethod, ResponseContentType} from '../enums/client-request-method.enum';

@Injectable()
export class RestApiService {

  baseUri: string = null;

  constructor(private _http: HttpClient,
              private _handler: CommonApiErrorHandlerService) {
    this.baseUri = runtimeConfig().api.protocol + '://' + runtimeConfig().api.hostName + ':' + runtimeConfig().api.port + '/';
  }

  get(uri: string, params?: any, skipErrorHandling = false, headers?: string | { [name: string]: string | string[]; }, responseType?: ResponseContentType): Promise<any> {
    return this._sendRequest({
      method: ClientRequestMethod.GET,
      path: uri,
      params,
      headers,
      responseType,
      skipErrorHandling
    });
  }

  put(uri: string, body?: any, params?: any): Promise<any> {
    return this._sendRequest({
      method: ClientRequestMethod.PUT,
      path: uri,
      body,
      params
    });
  }

  post(uri: string, body?: any, skipErrorHandling = false, headers?: string | { [name: string]: string | string[]; }, params?: any): Promise<any> {
    return this._sendRequest({
      method: ClientRequestMethod.POST,
      path: uri,
      body,
      params,
      headers,
      skipErrorHandling
    });
  }

  delete(uri: string, body?: any): Promise<any> {
    return this._sendRequest({
      method: ClientRequestMethod.DELETE,
      path: uri,
      body
    });
  }

  patch(uri: string, body?: any): Promise<any> {
    return this._sendRequest({
      method: ClientRequestMethod.PATCH,
      path: uri,
      body: body
    });
  }

  public prepareUri(...parts: string[]): string {
    return parts.map(p => p.replace(/^\/+/, '')).map(p => p.replace(/\/+$/, '')).join('/');
  }

  public send(config: RequestConfiguration): Promise<Response> {
    return this._sendRequest(config);
  }

  public getBaseuri(): string {
    return this.baseUri;
  }

  private _sendRequest(config: RequestConfiguration): Promise<Response> {
    //noinspection TypeScriptUnresolvedFunction
    if (_.isNil(config.method)) {
      throw new Error('Bad request configuration. Method option is required');
    }

    const options = new ClientRequestOptions();
    options.responseType = config.responseType;

    options.headers = config.headers ? new HttpHeaders(config.headers) : new HttpHeaders();

    let method = <ClientRequestMethod>config.method;
    let isBodyRequired = method !== ClientRequestMethod.GET &&
      method !== ClientRequestMethod.OPTIONS &&
      method !== ClientRequestMethod.DELETE &&
      method !== ClientRequestMethod.PATCH &&
      method !== ClientRequestMethod.PUT;

    if (isBodyRequired) {
      if (!_.isObject(config.body)) {
        console.error('Trying to send empty body');
        throw new Error('Trying to send empty body');
      }
    }
    options.body = config.body;

    options.method = config.method;

    let url = this._prepareUrl(config.path);

    if (config.params) {
      options.params = new HttpParams();
      Object.keys(config.params).forEach(k => {
        let paramValue = config.params[k];
        if (paramValue instanceof GetParamsExpandableArrayWrapper) {
          paramValue.content.forEach(it => options.params = options.params.append(k, it));
        } else {
          options.params = options.params.set(k, paramValue);
        }
      });
    }

    let promise = this._http
      .request(options.method, url, options)
      .toPromise();

    promise = promise.catch((r: Response) => this._handler.handleError(r, config.skipErrorHandling));
    return promise;

  }

  private _prepareUrl(path: string) {
    if (path && path.match(/^(https?:\/\/)?(localhost|([0-9]{1,3}\.){3}[0-9]{1,3}|([a-z]+\.)+[a-z]+):[0-9]{1,5}/ig)) {
      return path;
    }
    return path.replace(/^\/+/, '').replace(/\/+$/, '');
  }
}

